import * as React from 'react'
import { createEditor, Editor, Range, Transforms } from 'slate'
import { Editable, ReactEditor, Slate, withReact } from 'slate-react'
import classNames from 'classnames'
import { uniqBy } from 'lodash'

import { pipe } from '../../lib/functional/pipe.no-deps'
import type { UserProfile } from '../users/types.no-deps'
import { RichTextNode } from './TextNode'
import { getPathPrefix } from '../../webcomponents/helpers/href'
import type { SupportedLanguage } from '../../i18n/i18n.no-deps'
import { type Assignable, fuzzyFilter, isUser } from '../schedule/assignee.no-deps'
import { isMentionNode, isNewLineNode, isUnitAttributeNode } from './helpers.no-deps'
import type { RichTextParagraph, CustomElement, MentionNode, UnitAttributeNode } from './types.no-deps'
import { type UnitAttribute, fuzzyFilter as fuzzyFilterForUnitAttributes } from '../units/types.no-deps'

export interface Props {
    content: RichTextParagraph[]
    contentKey: string
    onChange?: (value: RichTextParagraph[]) => void
    onSubmit?: () => void
    users: UserProfile[]
    unitAttributes: UnitAttribute[]
    placeholder?: string
    disabled?: boolean
    readOnly?: boolean
    language?: SupportedLanguage
    usePlain?: boolean
    pre?: React.ReactNode
    post?: React.ReactNode
    action?: React.ReactNode
    isEditMode?: boolean
}

const MAX_USER_SUGGESTIONS = 5

const START_OF_TEXT = { path: [0, 0], offset: 0 }

// This regular expression looks for #hashtags in the input. It should capture
// the content of the hashtag (but not the octothorpe symbol itself) in group 1.
// If there is no content after the # then group 1 will be an empty string.
//
// We expect it to match:
// +-------------------+----------------------------------------------------+
// | Input             | Description                                        |
// +-------------------+----------------------------------------------------+
// | `#hashtag`        | Simple hashtag                                     |
// | `#hashtag `       | Exactly one trailing space                         |
// | ` #hashtag`       | Leading space                                      |
// | ` #hashtag `      | Leading and trailing space                         |
// | `      #hashtag`  | N leading spaces                                   |
// | `#hæşhțāğ`        | Non-ASCII Unicode characters                       |
// | `#hash tag`       | Infix spaces                                       |
// | `#hash123`        | Numbers                                            |
// | `#`               | No characters after the hash                       |
// +-------------------+----------------------------------------------------+
//
// We do not expect it to match:
// +-------------------+----------------------------------------------------+
// | Input             | Description                                        |
// +-------------------+----------------------------------------------------+
// | `#hashtag  `      | N trailing spaces, where N > 1                     |
// | `#hash123!@`      | Punctuation                                        |
// | `#hash tag!@`     | Punctuation and spaces                             |
// +-------------------+----------------------------------------------------+
//
// This expression looks more complicated than you may expect, and you may be
// looking at the redundant space matching and wondering why it is there.
// This is because it has been crafted to avoid the ReDOS vulnerability.
// https://en.wikipedia.org/wiki/ReDoS
// See also this bug ticket: https://zaptic.atlassian.net/browse/ZAP-3397
//
// The ReDOS vulnerability arises when the regular expression engine has to
// backtrack many times to find a match. This can lock up or crash the browser.
// This updated expression should run with linear time complexity, and is broken
// down as follows:
//
// +-------------------+----------------------------------------------------+
// | Expression        | Description                                        |
// +-------------------+----------------------------------------------------+
// | (?:^| )           | The start of a string, or a leading space, in a    |
// |                   |   non-capturing group                              |
// | #                 | Literal octothorpe symbol                          |
// | (                 | Start of the capturing group - this is Group 1     |
// | (?:               | Start of a non-capturing group - call this Group X |
// | [\p{L}\p{N}]+     | One or more Unicode letters or numbers             |
// | (?:               | Start of another non-capturing group - call this   |
// |                   |   Group Y                                          |
// |  [\p{L}\p{N}]+    | One or more Unicode letters or numbers, preceeded  |
// |                   |   by a space                                       |
// | )* ?              | End of Y, which can occur zero or more times, and  |
// |                   |   has an optional trailing space                   |
// | )?                | End of X, which is also optional                   |
// | )                 | End of Group 1                                     |
// | $                 | End of the string                                  |
// +-------------------+----------------------------------------------------+
//
// This should be ReDOS safe. If you modify this expression, ensure it is still
// safe by using the checker at https://devina.io/redos-checker
const hashRegExp = /(?:^| )#((?:[\p{L}\p{N}]+(?: [\p{L}\p{N}]+)* ?)?)$/u

// Happily, this is already ReDOS-safe :)
const mentionRegExp = /(^| )@([\p{L}\p{N}]*)$/u

// Overwrite built in function in the editor when our nodes are used
// Very naïve approach that assumes we just need the value `true` for everything
const customFlag = (builtIn: (x: CustomElement) => boolean) => (element: CustomElement) =>
    element.type === 'mention' || element.type === 'unit-attribute' || element.type === 'new-line'
        ? true
        : builtIn(element)

const withCustomNodes = (editor: ReactEditor) => {
    const { isInline, isVoid } = editor
    editor.isInline = customFlag(isInline)
    editor.isVoid = customFlag(isVoid)
    return editor
}

const getRangeFromCursorForRegExp = (editor: Editor, regexp: RegExp) => {
    const text = getTypingForRegExp(editor, regexp)
    if (text == null) return
    const cursor = Range.start(editor.selection)
    const point = Editor.before(editor, cursor, { distance: text.length + 1 })
    return Editor.range(editor, cursor, point)
}

const getTypingForRegExp = (editor: Editor, regexp: RegExp) => {
    // editor.selection is defined when the editor has focus
    // Range.isCollapsed(selection) is true when we are in insert mode ie there is nothing selected
    if (!(editor.selection || Range.isCollapsed(editor.selection))) {
        return null
    }
    const cursor = Range.start(editor.selection)

    // The # or @ symbol isn't treated as a normal character in slate so we need to
    // test the whole line instead of only the previous word
    const beginningOfLine = Editor.before(editor, cursor, { unit: 'line' })
    const beforeText = Editor.string(editor, Editor.range(editor, cursor, beginningOfLine))
    const beforeMatch = beforeText && beforeText.match(regexp)
    return beforeMatch ? beforeMatch[1] : null
}

const insertNode = (editor: Editor, element: CustomElement) => {
    const space = { text: ' ' }
    const newNodes = [element, space]

    Transforms.select(
        editor,
        getRangeFromCursorForRegExp(editor, element.type === 'mention' ? mentionRegExp : hashRegExp)
    )
    // insert node with a space at the end i.e '@name ' or '#attribute '
    Transforms.insertNodes(editor, newNodes)
    Transforms.move(editor, { distance: newNodes.length })
}

type KeyDeps = {
    search: { mention: { term: string }; hash: { term: string } }
    selectedIndex: number
    setSelectedIndex: (x: number) => void
    users: UserProfile[]
    handleSelectUser: (index: number) => void
    unitAttributes: UnitAttribute[]
    handleSelectUnitAttribute: (index: number) => void
    handleShiftEnter: () => void
    onSubmit: () => void
    editor: Editor
}
const handleKeyDown =
    ({
        search,
        selectedIndex,
        setSelectedIndex,
        users,
        handleSelectUser,
        unitAttributes,
        handleSelectUnitAttribute,
        handleShiftEnter,
        onSubmit,
        editor,
    }: KeyDeps) =>
    (event: React.KeyboardEvent) => {
        if (event.ctrlKey && event.key === 'Enter') {
            event.preventDefault()
            // Submitting usually clears the field, slate crashes if the cursor is
            // after the end of the text so reset the cursor to the beginning
            Transforms.setSelection(editor, { anchor: START_OF_TEXT, focus: START_OF_TEXT })
            onSubmit()
            return
        }

        if (event.shiftKey && event.key === 'Enter') {
            event.preventDefault()
            handleShiftEnter()
            return
        }

        const hasMentionSearch = search.mention.term !== null
        const hasHashSearch = search.hash.term !== null

        if (!hasMentionSearch && !hasHashSearch) return

        let listLength = null
        let handle = null

        listLength = hasMentionSearch ? users.length : unitAttributes.length
        handle = hasMentionSearch ? handleSelectUser : handleSelectUnitAttribute

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault()
                setSelectedIndex(Math.min(selectedIndex + 1, listLength - 1))
                break
            case 'ArrowUp':
                event.preventDefault()
                setSelectedIndex(Math.max(selectedIndex - 1, 0))
                break
            case 'Tab':
            case 'Enter':
                event.preventDefault()
                handle(selectedIndex)
                break
        }
    }

const plainToRich = (text: string) => [{ children: [{ text }] }]
// We assume there are no user mentions and unit attributes, if so this will remove them
const richToPlain = (nodes: RichTextParagraph[], assignables: Assignable[], unitAttributes: UnitAttribute[]) => {
    const users = assignables.filter(isUser)

    return nodes
        .map((node: RichTextParagraph) =>
            node.children
                .map((node) => {
                    if (isMentionNode(node)) {
                        const user = users.find((user) => user.userId === node.userId)
                        return `@${user?.name}`
                    } else if (isUnitAttributeNode(node)) {
                        const attribute = unitAttributes.find(
                            (unit) => unit.name === node.name && unit.unitTypeId === node.unitTypeId
                        )
                        return `#${attribute.name}`
                    } else if (isNewLineNode(node)) {
                        return '\n'
                    } else {
                        return node.text
                    }
                })
                .join(' ')
        )
        .join('\n')
}

const updateEventsOnList = (
    node: EventTarget,
    oldHandleHover: React.MutableRefObject<any>,
    oldHandleSelect: React.MutableRefObject<any>,
    setSelectedIndex: (idx: number) => void,
    handleSelect: (idx: number) => void
) => {
    if (node !== null) {
        if (oldHandleHover.current) {
            node.removeEventListener('onHovered', oldHandleHover.current)
        }
        const newHandleHover = ({ detail }: CustomEvent) => setSelectedIndex(detail)
        node.addEventListener('onHovered', newHandleHover)
        oldHandleHover.current = newHandleHover

        if (oldHandleSelect.current) {
            node.removeEventListener('onSelected', oldHandleSelect.current)
        }
        const newHandleSelect = ({ detail }: CustomEvent) => handleSelect(detail)
        node.addEventListener('onSelected', newHandleSelect)
        oldHandleSelect.current = newHandleSelect
    }
}
interface UserListProps {
    users: Assignable[]
    above?: boolean
    setSelectedIndex: (idx: number) => void
    handleSelectUser: (idx: number) => void
    selectedIndex: number
    language: SupportedLanguage
}

const UserList = (props: UserListProps) => {
    if (props.users == null) {
        return null
    }
    const oldHandleHover = React.useRef(null)
    const oldHandleSelect = React.useRef(null)
    const userList = React.useCallback(
        (node: EventTarget) =>
            updateEventsOnList(node, oldHandleHover, oldHandleSelect, props.setSelectedIndex, props.handleSelectUser),
        [props.setSelectedIndex, props.handleSelectUser]
    )

    return (
        <zaptic-popover above={props.above}>
            <zaptic-user-list
                ref={userList}
                items={JSON.stringify(props.users)}
                selected={JSON.stringify(props.selectedIndex)}
                language={JSON.stringify(props.language)}
            ></zaptic-user-list>
        </zaptic-popover>
    )
}

interface UnitAttributeListProps {
    unitAttributes: UnitAttribute[]
    above?: boolean
    setSelectedIndex: (idx: number) => void
    handleSelectUnitAttribute: (idx: number) => void
    selectedIndex: number
    language: SupportedLanguage
}

const UnitAttributeList = (props: UnitAttributeListProps) => {
    if (props.unitAttributes == null) {
        return null
    }
    const oldHandleHover = React.useRef(null)
    const oldHandleSelect = React.useRef(null)
    const unitAttributeList = React.useCallback(
        (node: EventTarget) =>
            updateEventsOnList(
                node,
                oldHandleHover,
                oldHandleSelect,
                props.setSelectedIndex,
                props.handleSelectUnitAttribute
            ),
        [props.setSelectedIndex, props.handleSelectUnitAttribute]
    )

    return (
        <zaptic-popover above={props.above}>
            <zaptic-string-list
                ref={unitAttributeList}
                items={JSON.stringify(props.unitAttributes)}
                selected={JSON.stringify(props.selectedIndex)}
                language={JSON.stringify(props.language)}
            ></zaptic-string-list>
        </zaptic-popover>
    )
}

const RichTextEntry = (props: Props) => {
    const pathPrefix = React.useCallback(getPathPrefix, [])()
    const [search, setSearch] = React.useState({ mention: { term: null }, hash: { term: null } })
    const [selectedIndex, setSelectedIndex] = React.useState(0)

    const users =
        search.mention.term != null
            ? (fuzzyFilter(props.users, search.mention.term).slice(0, MAX_USER_SUGGESTIONS) as UserProfile[])
            : null

    const unitAttributes =
        search.hash.term != null
            ? fuzzyFilterForUnitAttributes(uniqBy(props.unitAttributes, 'name'), search.hash.term)
            : null

    const editor = React.useMemo(() => pipe(createEditor(), withReact, withCustomNodes), [])
    const renderElement = React.useCallback(
        (nodeProps) => (
            <RichTextNode
                users={props.users}
                language={props.language}
                unitAttributes={props.unitAttributes}
                isEditMode={props.isEditMode}
                {...nodeProps}
            />
        ),
        [props.users, props.unitAttributes, props.isEditMode]
    )

    // Once the editor has lost focus it loses the cursor position, store it so
    // we can programmatically refocus later
    const editorSelection = React.useRef(editor.selection)
    if (ReactEditor.isFocused(editor)) {
        editorSelection.current = editor.selection
    }

    const prevMentionSearch = React.useRef(search.mention.term)
    React.useEffect(() => {
        // Reset the selection when a user clears a search
        if (!search.mention.term && prevMentionSearch.current) {
            setSelectedIndex(0)
        }
        // The user has refined the search and now there are fewer items in the
        // list; keep the selection valid
        if (users && users.length <= selectedIndex) {
            setSelectedIndex(users.length - 1)
        }

        prevMentionSearch.current = search.mention.term
    }, [search.mention.term])

    const prevHashSearch = React.useRef(search.hash.term)
    React.useEffect(() => {
        // Reset the selection when a user clears a search
        if (!search.hash.term && prevHashSearch.current) {
            setSelectedIndex(0)
        }
        // The user has refined the search and now there are fewer items in the
        // list; keep the selection valid
        if (unitAttributes && unitAttributes.length <= selectedIndex) {
            setSelectedIndex(unitAttributes.length - 1)
        }

        prevHashSearch.current = search.hash.term
    }, [search.hash.term])

    const handleSelectUser = React.useCallback(
        (index: number) => {
            // When clicking a user the text box loses focus, we need to
            // restore that before we can perform any operations on it
            ReactEditor.focus(editor)
            editor.selection = editorSelection.current

            const mention: MentionNode = { type: 'mention', userId: users[index].userId, children: [{ text: '' }] }

            insertNode(editor, mention)
            setSelectedIndex(0)
        },
        [editor, users]
    )

    const handleSelectUnitAttribute = React.useCallback(
        (index: number) => {
            // When clicking an attribute  the text box loses focus, we need to
            // restore that before we can perform any operations on it
            ReactEditor.focus(editor)
            editor.selection = editorSelection.current
            const data = unitAttributes[index]
            if (!data) {
                return handleShiftEnter()
            }
            const hash: UnitAttributeNode = {
                type: 'unit-attribute',
                unitTypeId: data.unitTypeId,
                name: data.name,
                children: [{ text: '' }],
            }

            insertNode(editor, hash)
            setSelectedIndex(0)
        },
        [editor, unitAttributes]
    )

    const handleShiftEnter = React.useCallback(() => {
        editor.selection = editorSelection.current

        const newLineNode = { type: 'new-line', children: [{ text: '' }] }
        const newNode = [newLineNode]

        // Insert new line node
        Transforms.insertNodes(editor, newNode)

        Transforms.move(editor, { distance: newNode.length })
    }, [editor])

    const handleChange = React.useCallback(
        (value) => {
            if (props.onChange) {
                props.onChange(value)
            }
            if (ReactEditor.isFocused(editor)) {
                const mention = getTypingForRegExp(editor, mentionRegExp)
                const hash = getTypingForRegExp(editor, hashRegExp)
                setSearch({ mention: { term: mention }, hash: { term: hash } })
            }
        },
        [editor, props.onChange]
    )

    const onKeyDown = React.useCallback(
        handleKeyDown({
            search,
            selectedIndex,
            setSelectedIndex,
            users,
            handleSelectUser,
            unitAttributes,
            handleSelectUnitAttribute,
            handleShiftEnter,
            onSubmit: props.onSubmit,
            editor,
        }),
        [
            search,
            selectedIndex,
            setSelectedIndex,
            users,
            handleSelectUser,
            unitAttributes,
            handleSelectUnitAttribute,
            handleShiftEnter,
        ]
    )

    const contentDiv = React.useRef(null)
    const positionOnPage = contentDiv.current?.getBoundingClientRect().top ?? 0
    const renderListAbove = positionOnPage >= window.innerHeight / 2
    const displayUnitAttributesList = props.unitAttributes.length !== 0
    const displayUsersList = props.users.length !== 0

    return (
        <>
            <link rel="stylesheet" type="text/css" href={`${pathPrefix}shared/css/zaptic-rich-text.css`} />
            {displayUnitAttributesList && (
                <UnitAttributeList
                    above
                    unitAttributes={unitAttributes}
                    setSelectedIndex={setSelectedIndex}
                    handleSelectUnitAttribute={handleSelectUnitAttribute}
                    selectedIndex={selectedIndex}
                    language={props.language}
                />
            )}
            {displayUsersList && renderListAbove && (
                <UserList
                    above
                    users={users}
                    setSelectedIndex={setSelectedIndex}
                    handleSelectUser={handleSelectUser}
                    selectedIndex={selectedIndex}
                    language={props.language}
                />
            )}
            <div className="comment-editable-main" ref={contentDiv}>
                {/* Use key to force update of slate as `value` prop no longer supports externally updating
                https://github.com/ianstormtaylor/slate/releases/tag/slate-react%400.67.0 */}
                <Slate editor={editor} value={props.content} onChange={handleChange} key={props.contentKey}>
                    {props.pre ?? <div className="richtext-entry-pre" />}
                    <div className={classNames({ 'comment-editable-wrap': !props.readOnly })}>
                        <Editable
                            className="comment-editable"
                            renderElement={renderElement}
                            onKeyDown={onKeyDown}
                            placeholder={props.placeholder}
                            readOnly={props.readOnly || props.disabled}
                        />
                        {props.action ?? <div className="richtext-entry-action" />}
                    </div>
                    {props.post ?? <div className="richtext-entry-post" />}
                </Slate>
            </div>
            {displayUsersList && !renderListAbove && (
                <UserList
                    users={users}
                    setSelectedIndex={setSelectedIndex}
                    handleSelectUser={handleSelectUser}
                    selectedIndex={selectedIndex}
                    language={props.language}
                />
            )}
        </>
    )
}

const PlainTextEntry = (props: Props) => {
    const pathPrefix = React.useCallback(getPathPrefix, [])()

    const value = richToPlain(props.content, props.users, props.unitAttributes)
    const doChange = (evt: any) => {
        if (!props.onChange) {
            return
        }
        const rich = plainToRich(evt.target.value)
        props.onChange(rich)
    }

    return (
        <>
            <link rel="stylesheet" type="text/css" href={`${pathPrefix}shared/css/zaptic-comment-add.css`} />
            <div className="comment-editable-main">
                {props.pre ?? <div className="richtext-entry-pre" />}
                <div className={classNames({ 'comment-editable-wrap': !props.readOnly })}>
                    {props.readOnly && <div className="comment-editable-read-only">{value}</div>}
                    {!props.readOnly && (
                        <textarea
                            className={classNames('comment-editable', { disabled: props.disabled })}
                            value={value}
                            onChange={doChange}
                            placeholder={props.placeholder}
                        />
                    )}
                    {props.action ?? <div className="richtext-entry-action" />}
                </div>
                {props.post ?? <div className="richtext-entry-post" />}
            </div>
        </>
    )
}

const RichTextWrapper = (props: Props) => {
    return props.usePlain ? <PlainTextEntry {...props} /> : <RichTextEntry {...props} />
}

export default RichTextWrapper
